/* eslint-disable */
/*
 * TestFileGenerator — shared client utilities for the `samples` tool group:
 *   - dummy-file-generator   (streams Uint8Array → file)
 *   - sample-video           (FFmpeg WASM custom)
 *   - sample-audio           (WebAudio + audio-exporter)
 *   - test-image-pack        (Canvas → blob)
 *   - test-pdf               (pdf-lib)
 *   - corrupted-file-generator
 *   - codec-sample-pack
 *
 * Plain ES5 — must parse on Yandex Browser ≤25 and Samsung Internet ≤14.
 *
 * Drained via window.__drainSharedModule('TestFileGenerator') at the end.
 * Layout pre-registers the stub in timbrica-core.js so tool scripts calling
 * TestFileGenerator.* before this file resolves work (defer-onerror race).
 */
(function () {
    'use strict';

    var SIZE_UNITS = {
        b: 1, kb: 1024, mb: 1024 * 1024, gb: 1024 * 1024 * 1024,
        k: 1000, m: 1000 * 1000, g: 1000 * 1000 * 1000,
    };

    function formatBytes(n) {
        if (!isFinite(n) || n < 0) return '—';
        if (n < 1024) return n + ' B';
        if (n < 1024 * 1024) return (n / 1024).toFixed(n < 10 * 1024 ? 2 : 1) + ' KB';
        if (n < 1024 * 1024 * 1024) return (n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 2 : 1) + ' MB';
        return (n / 1024 / 1024 / 1024).toFixed(2) + ' GB';
    }

    function parseSize(str) {
        if (typeof str === 'number') return str | 0;
        if (!str) return 0;
        var m = String(str).trim().toLowerCase().replace(/\s+/g, '').match(/^(\d+(?:\.\d+)?)([a-z]+)?$/);
        if (!m) return 0;
        var n = parseFloat(m[1]);
        var unit = (m[2] || 'b').replace('ib', 'b');
        var mult = SIZE_UNITS[unit];
        if (!mult) return 0;
        return Math.round(n * mult);
    }

    // ── Where the bytes can live, and therefore how many of them there can be ──
    //
    // The old shape of this function was two constants: 1 GiB without
    // showSaveFilePicker, 10 GiB with it. Neither was measured, and the 1 GiB
    // one was not even the operative limit — the operative limit was that the
    // blob lane holds the WHOLE FILE IN RAM TWICE (chunks array + the Blob copy
    // over it). Measured on real Chromium, private bytes per generated byte:
    //
    //     chunks[] then new Blob(chunks) .... 1.99–2.03x   <- what shipped
    //     Blob grown chunk by chunk ......... 1.02x
    //
    // So a 100 MB request on the reporter's 2 GB phone (bug report 2026-08-30,
    // Galaxy A01, Samsung Internet, deviceMemory 2) cost ~200 MB of
    // unreclaimable memory, and the page's own advertised mobile ceiling of
    // 1 GiB would have demanded ~2 GiB — more RAM than the whole device has.
    //
    // Now the ceiling is derived from where the bytes actually go:
    //   fsa   — straight into the user's chosen file; nothing is held, so the
    //           limit is their disk, and a disk that fills up says so.
    //   blob  — the only lane that spends RAM; capped by the device's own
    //           memory and by Chromium's hard Blob wall.
    //
    // ⛔ A THIRD LANE WAS BUILT, MEASURED AND REMOVED: staging the file in OPFS
    // and delivering a disk-backed File. It is the obvious idea — one chunk
    // live in JS, ceiling from navigator.storage.estimate() instead of a guess
    // — and it loses on the only number that matters here. Peak private bytes
    // per generated byte, same instrument, same machine:
    //
    //     grown Blob ................................ 1.00x  (700 MB), 1.00x (1800 MB)
    //     OPFS via createWritable ................... 2.22x  (700 MB), 1.62x (1800 MB)
    //     OPFS via createSyncAccessHandle in a worker  2.23x  (700 MB), 1.96x (1800 MB)
    //
    // Both OPFS variants settle back to ~1x afterwards and free completely once
    // the File is dropped — but the device dies at the PEAK, not at the settled
    // value, and the peak is where OPFS is twice the blob lane. Staging would
    // have made the low-memory phone in the report WORSE while looking like the
    // careful fix. (It also hit QuotaExceededError at 3 GB, where the blob lane
    // does not, because the write needs the data plus its swap copy.)
    var GiB = 1024 * 1024 * 1024;
    // Chromium cannot READ a Blob of 2 GiB or more — pinned in
    // timbrica-core.js: 2 139 095 040 B reads fine, 2^31 throws
    // NotReadableError on head and tail alike. A file we cannot read is a file
    // we cannot deliver, so the blob lane must stop below it.
    var BLOB_HARD_WALL = 2139095040;
    // How much of a device's RAM one download may occupy. deviceMemory is
    // coarse (2 / 4 / 8) and is the only figure browsers give us; a quarter of
    // it leaves the tab, the page and the browser room to survive the copy the
    // download manager makes on the way out.
    var MEM_SHARE = 0.25;

    function detectCaps() {
        var hasFsa = typeof window.showSaveFilePicker === 'function';
        var mem = 0;
        try { mem = Number(navigator.deviceMemory) || 0; } catch (_) { mem = 0; }
        // Firefox and Safari do not ship deviceMemory at all, so a flat
        // fallback would either halve the ceiling for a 32 GB desktop or
        // promise a phone memory it does not have. Split on the one signal
        // that is actually about the machine: a fine pointer with no touch is
        // a desktop. Being wrong low costs a refusal the user can read; being
        // wrong high costs a dead tab they cannot.
        if (!mem) {
            var desktopish = false;
            try {
                desktopish = !!(window.matchMedia && window.matchMedia('(pointer: fine)').matches)
                    && !(navigator.maxTouchPoints > 0);
            } catch (_) { desktopish = false; }
            mem = 0;   // stays 0 in the payload: unknown is not 4
            var assumed = desktopish ? 8 : 2;
            var memBudget = Math.floor(assumed * GiB * MEM_SHARE);
        } else {
            var memBudget = Math.floor(mem * GiB * MEM_SHARE);
        }
        var maxBlobBytes = Math.min(BLOB_HARD_WALL, memBudget);
        // With a picker the bytes never pass through us at all, so the limit is
        // the user's free disk — which we cannot read. 64 GiB is a sanity guard
        // against typos, not a claim about the device: prod shows requests for
        // 9 507 GB and 1e67 GB, and those should be refused before we try.
        var maxFsaBytes = hasFsa ? 64 * GiB : 0;
        return {
            hasFileSystemAccess: hasFsa,
            deviceMemoryGB: mem,
            maxBlobBytes: maxBlobBytes,
            // Kept for the old name: the point above which we stop paying RAM.
            maxSafeBlobBytes: maxBlobBytes,
            maxStreamBytes: Math.max(maxFsaBytes, maxBlobBytes),
            // Which lane the ceiling comes from, so the page can say WHY.
            ceilingLane: hasFsa ? 'fsa' : 'blob',
        };
    }

    function fillChunk(buf, mode, byteOffset) {
        var i, len = buf.length;
        if (mode === 'random') {
            var pos = 0;
            var STRIDE = 65536;
            while (pos < len) {
                var slice = buf.subarray(pos, Math.min(pos + STRIDE, len));
                crypto.getRandomValues(slice);
                pos += STRIDE;
            }
            return;
        }
        if (mode === 'text') {
            var text = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. ';
            var tlen = text.length;
            for (i = 0; i < len; i++) buf[i] = text.charCodeAt((byteOffset + i) % tlen);
            return;
        }
        if (mode === 'pattern') {
            for (i = 0; i < len; i++) buf[i] = (byteOffset + i) & 0xff;
            return;
        }
        buf.fill(0);
    }

    function yieldFrame() {
        return new Promise(function (resolve) {
            if (typeof requestAnimationFrame === 'function') requestAnimationFrame(function () { resolve(); });
            else setTimeout(resolve, 0);
        });
    }

    function generateDummyFile(opts) {
        opts = opts || {};
        // NB: `opts.size | 0` truncated to a SIGNED 32-bit int, so every request at
        // or above 2 GiB wrapped negative (2 GiB -> -2147483648, 4 GiB -> 0) and died
        // on the `size <= 0` guard below with "size must be positive" — while the tool
        // advertises up to 10 GiB via File System Access (maxStreamBytes). The real
        // ceiling check is two lines down; it can only work on an untruncated number.
        var size = Number(opts.size);
        size = isFinite(size) ? Math.floor(size) : 0;
        var fill = opts.fill || 'zero';
        var filename = opts.filename || 'dummy.bin';
        var chunkBytes = opts.chunkBytes || (4 * 1024 * 1024);
        var onProgress = opts.onProgress || function () {};
        var onLane = opts.onLane || function () {};
        // Hands the produced bytes back ONLY when the caller asked for them
        // (the retry rescue below). Returning them unconditionally would keep
        // the whole file alive after delivery.
        var onResult = opts.onResult || null;
        var signal = opts.signal;

        if (size <= 0) return Promise.reject(new Error('size must be positive'));
        var caps = detectCaps();
        if (size > caps.maxStreamBytes) {
            return Promise.reject(new Error('size exceeds browser limit (' + formatBytes(caps.maxStreamBytes) + ')'));
        }

        // ── Lane ladder: the cheapest lane that can actually hold the file ──
        //
        // It used to be inverted: the blob lane (whole file in RAM, twice, and
        // an <a download> that cannot report back) served everything up to
        // 1 GiB, and the picker lane was reached for only ABOVE that — the
        // better channel was used only where the worse one had already broken.
        //
        // Now: RAM decides. Below the device's memory budget the grown Blob is
        // the cheapest thing available (measured 1.00x the file, against 2.0x
        // for the shape that shipped and 2.2x for OPFS staging — see the
        // rejected third lane above). Past that budget, or past Chromium's
        // 2 GiB unreadable-Blob wall, RAM is not an option at all and the only
        // honest answer is the picker, where the bytes go straight to the disk
        // and close() is a real write receipt.
        var keep = function (payload) { if (onResult) { try { onResult(payload); } catch (_) {} } return payload; };
        if (size > caps.maxBlobBytes && caps.hasFileSystemAccess) {
            onLane('fsa');
            return generateViaFSA(size, fill, filename, chunkBytes, onProgress, signal);
        }
        onLane('blob');
        return generateViaBlob(size, fill, filename, chunkBytes, onProgress, signal, keep);
    }

    function generateViaFSA(size, fill, filename, chunkBytes, onProgress, signal) {
        var extMatch = /\.[a-z0-9]+$/i.exec(filename);
        var ext = extMatch ? extMatch[0].slice(1).toLowerCase() : 'bin';
        return window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'Test file', accept: makeAcceptForExt(ext) }],
        }).then(function (handle) {
            return handle.createWritable();
        }).then(function (writer) {
            var written = 0;
            var buf = new Uint8Array(chunkBytes);
            function step() {
                if (signal && signal.aborted) {
                    return writer.abort().then(function () { return false; });
                }
                var remaining = size - written;
                if (remaining <= 0) {
                    return writer.close().then(function () {
                        onProgress(size, size);
                        if (window.__deliveredToDisk) window.__deliveredToDisk(filename, size, 'save_handle');
                        return true;
                    });
                }
                var thisChunk = Math.min(chunkBytes, remaining);
                var view = thisChunk === chunkBytes ? buf : buf.subarray(0, thisChunk);
                fillChunk(view, fill, written);
                return writer.write(view).then(function () {
                    written += thisChunk;
                    onProgress(written, size);
                    return yieldFrame().then(step);
                });
            }
            return step();
        });
    }

    function generateViaBlob(size, fill, filename, chunkBytes, onProgress, signal, keep) {
        return new Promise(function (resolve, reject) {
            // ── Grow the Blob instead of the JS array ───────────────────────
            // The old shape kept every chunk alive in `chunks` AND then copied
            // all of them into a Blob, so the peak was 2x the file. Appending
            // to a Blob does not copy the bytes already in it — a Blob is
            // immutable and reference-counted, so the previous one is
            // referenced, not duplicated. MEASURED (private bytes per
            // generated byte, real Chromium, control-validated instrument):
            //     chunks[] then new Blob(chunks) .... 1.99x  (100 MB), 1.99x (700 MB)
            //     grown chunk by chunk .............. 1.44x  (100 MB), 1.02x (700 MB)
            // at the same wall-clock (2889 ms vs 3010 ms for 700 MB).
            var blob = new Blob([], { type: 'application/octet-stream' });
            var written = 0;
            function step() {
                if (signal && signal.aborted) { blob = null; resolve(false); return; }
                var remaining = size - written;
                if (remaining <= 0) {
                    onProgress(size, size);
                    if (keep) keep(blob);
                    deliver(blob, filename).then(resolve, reject);
                    return;
                }
                var thisChunk = Math.min(chunkBytes, remaining);
                var buf = new Uint8Array(thisChunk);
                fillChunk(buf, fill, written);
                blob = new Blob([blob, buf], { type: 'application/octet-stream' });
                buf = null;
                written += thisChunk;
                onProgress(written, size);
                yieldFrame().then(step);
            }
            try { step(); } catch (e) { reject(e); }
        });
    }

    function makeAcceptForExt(ext) {
        var map = {
            bin: ['application/octet-stream'],
            dat: ['application/octet-stream'],
            iso: ['application/x-iso9660-image'],
            txt: ['text/plain'],
            zip: ['application/zip'],
            raw: ['application/octet-stream'],
        };
        var mimes = map[ext] || ['application/octet-stream'];
        var accept = {};
        accept[mimes[0]] = ['.' + ext];
        return accept;
    }

    // One door out, so the delivery primitive (gesture budget, readability
    // pre-flight, WebView routing, honest-failure notice) owns every result
    // this file produces. `false` from it means "not delivered" — cancelled
    // picker, held gesture, unreadable bytes — and must survive as false; the
    // caller shows a very different message for it.
    function deliver(payload, filename) {
        return Promise.resolve(safeBlobDownload(payload, filename)).then(function (ok) {
            return ok !== false;
        });
    }

    function safeBlobDownload(blob, filename) {
        if (typeof window.__downloadFile === 'function') {
            return Promise.resolve(window.__downloadFile(blob, filename));
        }
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(function () { URL.revokeObjectURL(url); }, 30000);
        return Promise.resolve(true);
    }

    function _send(event, slug, payload) {
        if (typeof window.__ae !== 'function') return;
        try {
            var data = { slug: slug };
            if (payload && typeof payload === 'object') {
                for (var k in payload) if (Object.prototype.hasOwnProperty.call(payload, k)) data[k] = payload[k];
            }
            window.__ae(event, data);
        } catch (_) {}
    }

    function recordDownload(slug, payload) { _send('tool_complete', slug, payload); }
    function recordAttempt(slug, payload)  { _send('tool_attempt',  slug, payload); }
    function recordError(slug, payload)    { _send('tool_error',    slug, payload); }
    function recordFeature(slug, payload)  { _send('feature_use',   slug, payload); }

    window.TestFileGenerator = {
        formatBytes: formatBytes,
        parseSize: parseSize,
        detectCaps: detectCaps,
        generateDummyFile: generateDummyFile,
        safeBlobDownload: safeBlobDownload,
        recordDownload: recordDownload,
        recordAttempt: recordAttempt,
        recordError: recordError,
        recordFeature: recordFeature,
    };

    if (typeof window.__drainSharedModule === 'function') {
        window.__drainSharedModule('TestFileGenerator');
    }
})();
